import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../hooks/useAuth'
import { userService } from '../../services/userService'
import { AdminUserDetail } from '../../types/auth'
import { Badge } from '../../components/ui/Badge'
import { tokens } from '../../styles/tokens'
import { PlanBadge } from './PlanBadge'
import { UserActionButtons } from './UserActionButtons'
import { useUserActions } from './useUserActions'
import { AiUsageSection } from './AiUsageSection'

// ─── Styles ───────────────────────────────────────────────
const BackButton = styled.button`
  background: none;
  border: none;
  color: ${tokens.purple600};
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;

  &:hover {
    text-decoration: underline;
  }
`

const HeaderCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${tokens.purple100};
  padding: 20px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`

const AvatarCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  color: white;
  font-size: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const HeaderBody = styled.div`
  flex: 1;
  min-width: 240px;
`

const Name = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: ${tokens.purple900};
`

const Muted = styled.p`
  font-size: 13px;
  color: ${tokens.purple400};
  margin-top: 2px;
`

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
`

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 14px;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${tokens.purple100};
  padding: 20px;
`

const CardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.purple900};
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Dl = styled.dl`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 8px 12px;
  font-size: 13px;

  dt {
    color: ${tokens.purple400};
  }
  dd {
    color: ${tokens.purple900};
    word-break: break-word;
  }
`

const ErrorNote = styled.p`
  font-size: 14px;
  color: #c0436a;
`

// ─── Helpers ──────────────────────────────────────────────
const dateTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—')
const yesNo = (v: boolean | undefined) => (v ? 'Yes' : 'No')

function Details({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <Dl>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'contents' }}>
          <dt>{label}</dt>
          <dd>{value ?? '—'}</dd>
        </div>
      ))}
    </Dl>
  )
}

// ─── Page ─────────────────────────────────────────────────
export function UserDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { token } = useAuth()
  const svc = useMemo(() => userService(token), [token])

  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  // AI usage is shown for Pro users; for others an admin can still open the history
  const [showAiHistory, setShowAiHistory] = useState(false)

  const load = useCallback(async () => {
    try {
      setUser(await svc.get(id))
      setError(null)
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Could not load this user')
    }
  }, [svc, id])

  useEffect(() => {
    load()
  }, [load])

  const { actions, dialogs } = useUserActions(svc, load)
  const goBack = useCallback(() => navigate('/users'), [navigate])

  if (error) {
    return (
      <>
        <BackButton onClick={goBack}>← Back to users</BackButton>
        <ErrorNote>{error}</ErrorNote>
      </>
    )
  }
  if (!user) return null

  const { subscription } = user
  const tier = subscription?.tier ?? 'free'
  const initials = (user.name ?? user.email)
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <>
      <BackButton onClick={goBack}>← Back to users</BackButton>

      <HeaderCard>
        <AvatarCircle>
          {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials}
        </AvatarCircle>
        <HeaderBody>
          <Name>{user.name ?? user.email}</Name>
          <Muted>
            {user.email}
            {user.handle ? ` · @${user.handle}` : ''}
          </Muted>
          <Badges>
            <PlanBadge tier={tier} />
            <Badge color={user.isAdmin ? 'purple' : 'gray'}>
              {user.isAdmin ? 'Admin' : 'User'}
            </Badge>
            <Badge color={user.active ? 'green' : 'red'}>
              {user.active ? 'Active' : 'Inactive'}
            </Badge>
            {user.supportRestricted && <Badge color="red">Support blocked</Badge>}
            {user.mustResetPassword && <Badge color="amber">Must reset pw</Badge>}
            {!user.emailVerified && <Badge color="red">Unverified</Badge>}
            {user.isPrivate && <Badge color="gray">Private profile</Badge>}
          </Badges>
          <Actions>
            <UserActionButtons item={user} actions={actions} />
          </Actions>
        </HeaderBody>
      </HeaderCard>

      <Cards>
        <Card>
          <CardTitle>Account</CardTitle>
          <Details
            rows={[
              ['User ID', <code key="id">{user.id}</code>],
              ['Display name', user.displayName],
              ['Locale', user.locale],
              ['Country', user.country],
              ['Email verified', yesNo(user.emailVerified)],
              ['Google login', yesNo(user.integrations.google)],
              ['Apple login', yesNo(user.integrations.apple)],
              ['Joined', dateTime(user.createdAt)],
              ['Last login', dateTime(user.lastLoginAt)],
            ]}
          />
        </Card>

        <Card>
          <CardTitle>Subscription</CardTitle>
          <Details
            rows={[
              ['Plan', <PlanBadge key="plan" tier={tier} />],
              ['Billing', subscription?.billingPeriod ?? '—'],
              ['Period ends', dateTime(subscription?.periodEnd)],
              [
                'Stripe customer',
                subscription?.stripeCustomer ? (
                  <a
                    key="stripe"
                    href={`https://dashboard.stripe.com/customers/${subscription.stripeCustomer}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {subscription.stripeCustomer}
                  </a>
                ) : (
                  '—'
                ),
              ],
            ]}
          />
        </Card>

        <Card>
          <CardTitle>Profile & activity</CardTitle>
          <Details
            rows={[
              ['Recipes', user.stats.recipes],
              ['Followers', user.stats.followers],
              ['Following', user.stats.following],
              ['Private', yesNo(user.isPrivate)],
              ['Bio', user.bio],
              [
                'Links',
                user.socialLinks?.length
                  ? user.socialLinks.map(link => (
                      <div key={link.url}>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.label || link.url}
                        </a>
                      </div>
                    ))
                  : '—',
              ],
            ]}
          />
        </Card>
      </Cards>

      {tier === 'pro' || showAiHistory ? (
        <AiUsageSection
          svc={svc}
          userId={user.id}
          quotas={user.ai.quotas}
          dailyLimit={user.ai.dailyLimit}
        />
      ) : (
        <BackButton style={{ marginTop: 20 }} onClick={() => setShowAiHistory(true)}>
          Not on Pro — show AI usage history anyway
        </BackButton>
      )}

      {dialogs}
    </>
  )
}
